import { useEffect, useState } from "react";
import { createTicketComment, getTicketComments } from "../../services/ticket_service";
import "./WorkNotes.css";

function formatNoteTime(value) {
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? "" : date.toLocaleString([], {
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

export default function WorkNotes({ ticketType, ticketId }) {
	const [notes, setNotes] = useState([]);
	const [draft, setDraft] = useState("");
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		let cancelled = false;
		if (!ticketType || !ticketId) {
			setNotes([]);
			return undefined;
		}

		setLoading(true);
		setError("");
		getTicketComments(ticketType, ticketId)
			.then((rows) => {
				if (!cancelled) setNotes(Array.isArray(rows) ? rows : []);
			})
			.catch((nextError) => {
				if (!cancelled) setError(nextError?.message || "Failed to load work notes");
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [ticketType, ticketId]);

	async function handleAddNote() {
		const body = draft.trim();
		if (!body || saving) return;

		setSaving(true);
		setError("");
		try {
			const created = await createTicketComment(ticketType, ticketId, body);
			setNotes((current) => [created, ...current]);
			setDraft("");
		} catch (nextError) {
			setError(nextError?.message || "Failed to save work note");
		} finally {
			setSaving(false);
		}
	}

	return (
		<section className="it-work-notes" aria-label="Work Notes">
			<header className="it-work-notes-header">
				<div>
					<h3>Work Notes</h3>
					<p>Internal updates and handoff context for this ticket.</p>
				</div>
				<button type="button" className="it-work-notes-add" onClick={handleAddNote} disabled={saving || !draft.trim()}>
					{saving ? "Saving..." : "Add Work Note"}
				</button>
			</header>
			<textarea
				className="it-work-notes-composer"
				value={draft}
				onChange={(event) => setDraft(event.target.value)}
				placeholder="Record an update, investigation step, or handoff detail..."
				rows={4}
				disabled={saving}
			/>
			{error ? <p className="it-work-notes-error" role="alert">{error}</p> : null}
			{loading ? <p className="it-work-notes-empty">Loading work notes...</p> : notes.length === 0 ? (
				<p className="it-work-notes-empty">No work notes yet. Add the first update above.</p>
			) : (
				<div className="it-work-notes-list">
					{notes.map((note) => (
						<article key={note.id} className="it-work-note">
							<div className="it-work-note-meta">
								<strong>{note.author_name || "Unknown user"}</strong>
								<span>{formatNoteTime(note.created_at)}</span>
							</div>
							<p>{note.body}</p>
						</article>
					))}
				</div>
			)}
		</section>
	);
}